import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { RouteDriver } from '@/types/route.types';
import { useApi } from '@/hooks/useApi';
import { Phone, Mail, Car, ChevronLeft, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface RouteDriversListProps {
  routeId: string;
  drivers: RouteDriver[];
  loading?: boolean;
  onDriverRemoved?: () => void;
}

export function RouteDriversList({ routeId, drivers, loading, onDriverRemoved }: RouteDriversListProps) {
  const navigate = useNavigate();
  const { delete: deleteRequest, loading: removing } = useApi();
  const [driverToRemove, setDriverToRemove] = useState<RouteDriver | null>(null);

  const handleRemoveDriver = async () => {
    if (!driverToRemove) return;

    const driverProfileId = driverToRemove.driver_profile_id;
    const response = await deleteRequest(`/api/v1/settings/routes/${routeId}/drivers/${driverProfileId}`);

    if (response) {
      toast.success('تم إزالة السائق من المسار بنجاح');
      onDriverRemoved?.();
    }
    setDriverToRemove(null);
  };

  if (loading) {
    return <LoadingSkeleton type="card" rows={3} />;
  }

  if (drivers.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-12 w-12" />}
        title="لا يوجد سائقون معينون"
        description="لم يتم تعيين أي سائقين على هذا المسار بعد"
      />
    );
  }

  return (
    <>
      <div className="space-y-3">
        {drivers.map((driver) => (
          <div
            key={driver.id}
            className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                {/* Avatar */}
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-xl">
                  🚗
                </div>

                {/* Driver Info */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold">
                      {driver.driver_profile?.user?.name || 'غير معروف'}
                    </h4>
                    {!driver.is_active && (
                      <Badge variant="secondary" className="text-xs">
                        غير نشط
                      </Badge>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {driver.driver_profile?.user?.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />
                        <span>{driver.driver_profile.user.email}</span>
                      </div>
                    )}
                    {driver.driver_profile?.user?.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />
                        <span dir="ltr">{driver.driver_profile.user.phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Vehicle Info */}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {(driver.driver_profile?.vehicle_make || driver.driver_profile?.vehicle_model) && (
                      <div className="flex items-center gap-1">
                        <Car className="h-3.5 w-3.5" />
                        <span>
                          {[driver.driver_profile.vehicle_make, driver.driver_profile.vehicle_model]
                            .filter(Boolean)
                            .join(' ')}
                        </span>
                      </div>
                    )}
                    {driver.driver_profile?.vehicle_plate_number && (
                      <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">
                        {driver.driver_profile.vehicle_plate_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setDriverToRemove(driver)}
                  disabled={removing}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline me-1">إزالة</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/dashboard/drivers/${driver.driver_profile?.id}`)}
                >
                  <span className="hidden sm:inline">عرض</span>
                  <ChevronLeft className="h-4 w-4 sm:me-1" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!driverToRemove} onOpenChange={(open) => !open && setDriverToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>إزالة السائق من المسار</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من إزالة السائق "{driverToRemove?.driver_profile?.user?.name}" من هذا المسار؟
              يمكنك إعادة تعيينه لاحقاً.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveDriver}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              إزالة
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
